import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  Button,
  Badge,
  makeStyles,
  Spinner,
  Checkbox,
  Tooltip,
} from "@fluentui/react-components";
import { Checkmark20Regular, Link16Regular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import { Tool } from "../../models/Tool";
import { useGetAllCurrentPublishedTools } from "../../hooks/useTools";
import { TextSortAscending24Regular } from "@fluentui/react-icons";
import { TextSortDescending24Regular } from "@fluentui/react-icons";
import { getDocumentIcon } from "@skanska/vsa-file-icon";
import { VsaSearchBox } from "../VsaSearchBox";
import { Paginate } from "../Paginate";
import { QueryFilter } from "../../models/QueryFilter";
import { sanitizeFileName } from "../../services/ToolService";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    margin: "16px",
    height: "550px",
  },
  dialogtitle: {
    marginLeft: "16px",
  },
  alignStart: {
    alignSelf: "start",
  },

  toolContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  searchRow: {
    display: "flex",
    alignItems: "center",
    alignContent: "center",
    gap: "8px",
  },
  toolButton: {
    margin: "10px 0",
  },
  toolList: {
    overflowY: "auto",
  },
  checkBoxGroup: {
    borderBottom: "1px solid #ddd",
  },
  toolItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "6px 8px",
    cursor: "pointer",
    borderBottom: "1px solid #ddd",
    "&:hover": {
      backgroundColor: "#eee",
    },
  },
  toolName: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
});

type ToolPickerDialogProps = {
  selectedTools: Tool[];
  onSelect: (tool: Tool) => void;
  onRemove: (toolId: string) => void;
};

export default function ToolPickerDialog({
  selectedTools,
  onSelect,
  onRemove,
}: ToolPickerDialogProps) {
  const classes = useStyles();
  const { t } = useTranslation();
  const {
    data: toolsResult,
    isLoading,
    isRefetching,
    setSortByAscending,
    setQuery,
    setCurrentPage,
    setFilters,
    sortByAscending,
    query,
  } = useGetAllCurrentPublishedTools("title", true, [
    {
      fieldUniqueName: "active",
      values: ["true"],
    },
  ]);

  const [open, setOpen] = useState(false);
  const [showDocuments, setShowDocuments] = useState(true);
  const [showLinks, setShowLinks] = useState(true);
  const [showActive, setShowActive] = useState(true);
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => {
    const newFilters: QueryFilter[] = [];

    if (showDocuments && !showLinks) {
      newFilters.push({
        fieldUniqueName: "toolTypeLinkOrDocument",
        values: ["Document"],
      });
    } else if (!showDocuments && showLinks) {
      newFilters.push({
        fieldUniqueName: "toolTypeLinkOrDocument",
        values: ["Link"],
      });
    } else if (!showDocuments && !showLinks) {
      /* Silly but potentially real condition. */
      newFilters.push({
        fieldUniqueName: "toolTypeLinkOrDocument",
        values: ["none"],
      });
    }

    if (showActive && !showInactive) {
      newFilters.push({
        fieldUniqueName: "active",
        values: ["true"],
      });
    } else if (!showActive && showInactive) {
      newFilters.push({
        fieldUniqueName: "active",
        values: ["false"],
      });
    } else if (!showActive && !showInactive) {
      newFilters.push({
        fieldUniqueName: "active",
        values: ["none"],
      });
    }

    setFilters(newFilters);
  }, [showDocuments, showLinks, showActive, showInactive, setFilters]);

  return (
    <>
      <Dialog open={open} onOpenChange={(_, data) => setOpen(data.open)}>
        <DialogTrigger>
          <Button className={classes.toolButton}>{t("addTool")} </Button>
        </DialogTrigger>
        <DialogSurface>
          <DialogTitle className={classes.dialogtitle}>
            {t("addTool")}
          </DialogTitle>
          <DialogBody className={classes.root}>
            {isLoading || isRefetching || !toolsResult ? (
              <Spinner />
            ) : (
              <>
                <div className={classes.toolContainer}>
                  <div className={classes.searchRow}>
                    <VsaSearchBox
                      placeholder={t("searchTool")}
                      defaultValue={query}
                      onSearch={setQuery}
                      disableQueryParameter
                    />
                    <Button
                      size="medium"
                      onClick={() => setSortByAscending((sort) => !sort)}
                      icon={
                        sortByAscending ? (
                          <TextSortAscending24Regular />
                        ) : (
                          <TextSortDescending24Regular />
                        )
                      }
                      aria-label={
                        sortByAscending ? t("unsortAlpha") : t("sortAlpha")
                      }
                    />
                  </div>

                  <div className={classes.checkBoxGroup}>
                    <Checkbox
                      label={t("document")}
                      checked={showDocuments}
                      onChange={() => setShowDocuments((show) => !show)}
                    />

                    <Checkbox
                      label={t("link")}
                      checked={showLinks}
                      onChange={() => setShowLinks((show) => !show)}
                    />

                    <Checkbox
                      label={t("active")}
                      checked={showActive}
                      onChange={() => setShowActive((show) => !show)}
                    />
                    <Checkbox
                      label={t("inactive")}
                      checked={showInactive}
                      onChange={() => setShowInactive((show) => !show)}
                    />
                  </div>
                </div>
                <div className={classes.toolList}>
                  {toolsResult.items.map((tool) => (
                    <div
                      key={tool.toolId}
                      className={classes.toolItem}
                      onClick={() => onSelect(tool)}
                    >
                      <span className={classes.toolName}>
                        {tool.fileUniqueId ? (
                          <img
                            src={getDocumentIcon(tool.fileType)}
                            alt="type"
                            width={16}
                            height={16}
                            className={classes.alignStart}
                          />
                        ) : (
                          <Link16Regular />
                        )}
                        {tool.webUrl ? (
                          <Tooltip
                            content={
                              tool.fileUniqueId
                                ? sanitizeFileName(tool.webUrl)
                                : tool.adminDisplayName || ""
                            }
                            relationship="description"
                          >
                            <span>{tool.title}</span>
                          </Tooltip>
                        ) : (
                          <span>{tool.title}</span>
                        )}
                        {!tool.fieldValues?.booleans.active && (
                          <Badge appearance="outline" color="warning">
                            {t("inactive")}
                          </Badge>
                        )}
                      </span>
                      {selectedTools.some((t) => t.toolId === tool.toolId) && (
                        <Checkmark20Regular
                          onClick={(e) => {
                            e.stopPropagation();
                            onRemove(tool.toolId!);
                          }}
                        />
                      )}
                    </div>
                  ))}
                  {toolsResult.items.length === 0 && <p>{t("noResults")}</p>}
                </div>
                {toolsResult.numberOfPages > 1 && (
                  <Paginate
                    pageCount={toolsResult.numberOfPages}
                    onPageClick={setCurrentPage}
                    defaultPage={toolsResult.pageNumber}
                  />
                )}
              </>
            )}
          </DialogBody>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>{t("close")}</Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>
    </>
  );
}
